/**
 * SPR-571 — Filter config store.
 *
 * Owns the per-tab filter configs that drive the config-driven filter engine
 * (src/common/helpers/filter-engine). Eventually these come from BE Active
 * Admin via `GET /api/filter-configs/:tabKey`; until that endpoint exists this
 * store returns the bundled dummy config (src/common/configs) after a small
 * simulated latency so the loading/empty UX is exercised for real.
 *
 * Swap-in plan when BE ships: replace `fetchRemoteConfig` with the real HTTP
 * call and keep the bundled config as the offline fallback. See
 * docs/SPR-571-BE-GAPS.md §F.
 */

import axios from 'axios'
import { action, computed, observable, store } from '~/common/mobx.decorator'
import { BUNDLED_FILTER_CONFIGS } from '~/common/configs/match-filter-config'

// Flip to true once `GET /api/filter-configs/:tabKey` is live.
const BE_CONFIG_ENDPOINT_READY = true

const SIMULATED_LATENCY_MS = 250

/**
 * SPR-571 — filters HIDDEN on the FE because the backend has no real field for
 * them yet (verified live on STG 2026-06-03, per docs/SPR-571-BE-STATUS-FOR-FE.md):
 *   - 1.3 Credential Readiness            — BE blocked, source field TBD with Eitan.
 *   - 2.1 Engagement › Messages opened    — deferred (feed has no "opened" event).
 *
 * The other engagement controls (days-since-login, jobs viewed, jobs saved,
 * profile-updated-since) are LIVE on `MaestroWorkerFilter` and now shown.
 *
 * Hiding here (one chokepoint) strips them from every config — bundled OR the
 * BE endpoint — so no column / drawer panel / preset / breakdown chip renders
 * them. To re-enable, just remove the key(s) below.
 */
const HIDDEN_FILTER_KEYS = new Set(['credentialReadiness', 'engagement_messagesOpened'])

/**
 * Return a copy of `config` with every HIDDEN_FILTER_KEYS reference removed:
 *   - `filters`   — drop the hidden filter defs (no column / drawer control).
 *   - `breakdown` — drop chips pointing at a hidden filter.
 *   - `presets`   — drop the WHOLE preset if it references any hidden key. A
 *     preset's name/description describes its full recipe, so a partially
 *     stripped preset would be misleading; safer to hide it entirely until the
 *     filters are un-hidden.
 * Never mutates the input (the bundled config is shared).
 */
const sanitizeConfig = (config) => {
	if (!config || HIDDEN_FILTER_KEYS.size === 0) return config

	const filters = (config.filters || []).filter((f) => !HIDDEN_FILTER_KEYS.has(f.key))

	const presets = (config.presets || []).filter((preset) => !Object.keys(preset.filters || {}).some((key) => HIDDEN_FILTER_KEYS.has(key)))

	const breakdown = (config.breakdown || []).filter((chip) => !HIDDEN_FILTER_KEYS.has(chip.filterKey))

	return { ...config, filters, presets, breakdown }
}

@store()
class FilterConfigStore {
	// { [tabKey]: config }
	@observable configByTab = {}
	// { [tabKey]: 'loading' | 'ready' | 'error' }
	@observable statusByTab = {}

	@computed
	get isAnyLoading() {
		return Object.values(this.statusByTab).some((s) => s === 'loading')
	}

	getConfig = (tabKey) => this.configByTab[tabKey] || null

	getStatus = (tabKey) => this.statusByTab[tabKey] || 'idle'

	/**
	 * Fetch a tab's filter config from BE. The shared axios instance's baseURL
	 * is `<apiUrl>/api/public/v1` (see app.store / master.store), so the
	 * relative path `/filter_configs/:tabKey` resolves to the BE alias
	 * `GET /api/public/v1/filter_configs/:tab_key` (same handler as
	 * `GET /api/filter-configs/:tabKey`). On any error the caller
	 * (hydrateConfig) falls back to the bundled config, so prod still works
	 * when the endpoint isn't live yet.
	 */
	fetchRemoteConfig = async (tabKey) => {
		if (!BE_CONFIG_ENDPOINT_READY) {
			await new Promise((resolve) => setTimeout(resolve, SIMULATED_LATENCY_MS))
			const config = BUNDLED_FILTER_CONFIGS[tabKey]
			if (!config) throw new Error(`No bundled config for tab "${tabKey}"`)
			return config
		}
		const { data } = await axios.get(`/filter_configs/${tabKey}`)
		if (!data) throw new Error(`Empty filter-config response for tab "${tabKey}"`)
		return data
	}

	@action
	setConfig = (tabKey, config, status) => {
		this.configByTab = { ...this.configByTab, [tabKey]: sanitizeConfig(config) }
		this.statusByTab = { ...this.statusByTab, [tabKey]: status }
	}

	/**
	 * Load a tab's config once. Resolves to the config (remote, else bundled
	 * fallback so the UI is never blocked by a BE outage).
	 */
	@action
	hydrateConfig = async (tabKey) => {
		if (!tabKey) return null
		if (this.configByTab[tabKey]) return this.configByTab[tabKey]

		this.statusByTab = { ...this.statusByTab, [tabKey]: 'loading' }
		try {
			const config = await this.fetchRemoteConfig(tabKey)
			this.setConfig(tabKey, config, 'ready')
			return config
		} catch (e) {
			// Fall back to the bundled config so the tab still works offline.
			const fallback = BUNDLED_FILTER_CONFIGS[tabKey] || null
			this.setConfig(tabKey, fallback, fallback ? 'ready' : 'error')
			return fallback
		}
	}
}

export const filterConfigStore = new FilterConfigStore()
